import { useOrders } from "hooks/use-orders";
import { usePaths } from "BEDROCK/paths";
import { useState } from "react";
import styles from "./admin-orders-page.module.css";
import { StatusDropdown } from "BEDROCK/components/ui-status-dropdown/ui-status-dropdown";
import { ORDER_STATUSES, OrderStatus } from "types/order-status";

interface AdminOrdersPageProps {}

export const AdminOrdersPage = (props: AdminOrdersPageProps) => {
  const [statusFilter, setStatusFilter] = useState<OrderStatus>("created");
  const [pageSize, setPageSize] = useState<number>(50);
  const [currentPage, setCurrentPage] = useState<number>(0);

  const { orders, totalCount, isLoading, isError, refetch } = useOrders({
    status: statusFilter,
    limit: pageSize,
    offset: currentPage * pageSize,
  });
  const { navigateTo, orderDetail } = usePaths();

  const totalPages = Math.ceil(totalCount / pageSize);
  const hasNextPage = currentPage < totalPages - 1;
  const hasPrevPage = currentPage > 0;

  const handleStatusFilterChange = (newStatus: OrderStatus) => {
    setStatusFilter(newStatus);
    setCurrentPage(0); // Reset to first page when changing filters
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0); // Reset to first page when changing page size
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (hasPrevPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading orders...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          Error loading orders. Please try again.
          <button onClick={() => refetch()} className={styles.retryButton}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Orders</h1>
        <div className={styles.controls}>
          <div className={styles.statusFilter}>
            <StatusDropdown
              currentStatus={statusFilter}
              availableStatuses={ORDER_STATUSES}
              onStatusChange={handleStatusFilterChange}
              label="Filter by status"
            />
          </div>
          <button onClick={() => refetch()} className={styles.refreshButton}>
            Refresh
          </button>
        </div>
      </div>
      <div className={styles.orderList}>
        {orders && orders.length === 0 ? (
          <div className={styles.emptyState}>
            No orders found with status "{statusFilter}"
          </div>
        ) : (
          orders?.map((order) => (
            <div
              key={order.id}
              className={styles.orderItem}
              onClick={() => navigateTo(orderDetail(order.id))}
            >
              <div className={styles.orderHeader}>
                <div className={styles.orderNumber}>
                  {order.id_number || order.id.substring(0, 8)}
                </div>
                <div className={styles.orderStatus}>{order.status}</div>
              </div>
              <div className={styles.orderInfo}>
                <div className={styles.infoRow}>
                  <span className={styles.label}>User:</span>
                  <span className={styles.value}>
                    {order.user_full_name || order.user_email}
                  </span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.label}>Email:</span>
                  <span className={styles.value}>{order.user_email}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.label}>Created:</span>
                  <span className={styles.value}>
                    {new Date(order.created_at).toLocaleString()}
                  </span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.label}>Price:</span>
                  <span className={styles.value}>
                    £{(order.price / 100).toFixed(2)}
                  </span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.label}>Projects:</span>
                  <span className={styles.value}>
                    {order.project_ids.length} poster
                    {order.project_ids.length !== 1 ? "s" : ""}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      {orders && orders.length > 0 && (
        <div className={styles.paginationContainer}>
          <div className={styles.paginationInfo}>
            <span>
              Showing {currentPage * pageSize + 1} to{" "}
              {Math.min((currentPage + 1) * pageSize, totalCount)} of{" "}
              {totalCount} orders
            </span>
            <div className={styles.pageSizeSelector}>
              <label htmlFor="pageSize">Page size:</label>
              <select
                id="pageSize"
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className={styles.pageSizeSelect}
              >
                <option value={2}>2</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
          <div className={styles.paginationControls}>
            <button
              onClick={handlePrevPage}
              disabled={!hasPrevPage}
              className={styles.paginationButton}
            >
              Previous
            </button>
            <span className={styles.pageIndicator}>
              Page {currentPage + 1} of {totalPages || 1}
            </span>
            <button
              onClick={handleNextPage}
              disabled={!hasNextPage}
              className={styles.paginationButton}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
